import type {
  AppointmentRequest,
  AppointmentResponse,
  AvailabilityQuery,
  AvailabilityResponse
} from './types'

// ---------------------------------------------------------------------------
// /api contract client (§6). Same-origin in production (nginx proxies /api/*);
// proxied to the mock in dev. No auth, JSON bodies, GET uses query params.
// ---------------------------------------------------------------------------

/**
 * Normalized API error. The two endpoints use DIFFERENT envelopes (§6.3):
 *   - GET  /api/availabilities → 500 { message: "code" }       (single string)
 *   - POST /api/appointments   → 500 { message: ["code", ...] } (array)
 *                              → 400 { message: "time-not-available" } or other
 * We surface `codes` (always an array) plus the raw `status` so callers can
 * special-case `time-not-available` (§7.7) and otherwise show a generic retry.
 */
export class ApiError extends Error {
  status: number
  codes: string[]
  constructor(status: number, codes: string[]) {
    super(codes[0] || `http-${status}`)
    this.name = 'ApiError'
    this.status = status
    this.codes = codes
  }
  has(code: string): boolean {
    return this.codes.includes(code)
  }
}

function messageToCodes(message: unknown): string[] {
  if (typeof message === 'string') return [message]
  if (Array.isArray(message)) return message.map(String)
  return ['error']
}

async function parseError(res: Response): Promise<ApiError> {
  let body: unknown = null
  try {
    body = await res.json()
  } catch {
    /* non-JSON body */
  }
  const message = (body as { message?: unknown })?.message
  return new ApiError(res.status, messageToCodes(message))
}

export async function fetchAvailabilities(
  query: AvailabilityQuery,
  signal?: AbortSignal
): Promise<AvailabilityResponse> {
  const params = new URLSearchParams()
  params.set('start_date', query.start_date)
  params.set('date_count', String(query.date_count))
  params.set('profile', String(query.profile))
  if (query.sub_profile != null) params.set('sub_profile', String(query.sub_profile))
  params.set('status', query.status)
  if (query.region != null) params.set('region', String(query.region))
  if (query.zip_code != null) params.set('zip_code', query.zip_code)
  if (query.latitude != null) params.set('latitude', String(query.latitude))
  if (query.longitude != null) params.set('longitude', String(query.longitude))
  if (query.filter_by != null) params.set('filter_by', query.filter_by)
  if (query.scheduling != null) params.set('scheduling', query.scheduling)

  const res = await fetch(`/api/availabilities?${params.toString()}`, {
    headers: { Accept: 'application/json' },
    signal
  })
  if (!res.ok) throw await parseError(res)
  const data = (await res.json()) as AvailabilityResponse
  // Minimal shape guard: a malformed payload should surface as the handled
  // error state, not a render crash on data.entities.map.
  if (!data || !Array.isArray(data.entities)) throw new ApiError(res.status, ['malformed-response'])
  return data
}

/** Quiz-funnel lead capture (email + quiz context + attribution). */
export interface LeadRequest {
  email: string
  quiz: string
  outcome: string | null
  answers: Record<string, unknown>
  source: string | null
  href: string
  query: Record<string, string>
}

export async function postLead(body: LeadRequest): Promise<string> {
  const res = await fetch('/api/leads', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body)
  })
  if (!res.ok) throw await parseError(res)
  const json = (await res.json()) as { data?: { id?: string } }
  const id = json?.data?.id
  if (!id) throw new ApiError(res.status, ['error'])
  return id
}

export async function bookAppointment(body: AppointmentRequest): Promise<string> {
  const res = await fetch('/api/appointments', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body)
  })
  if (!res.ok) throw await parseError(res)
  const json = (await res.json()) as AppointmentResponse
  // Success signal is the presence of data.id (§6.2).
  const id = json?.data?.id
  if (!id) throw new ApiError(res.status, ['error'])
  return id
}
